import { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate, useSearchParams, Link } from "react-router-dom";
import API from "../lib/api";
import { Button } from "../components/ui/button";
import { PdfDownloadButton } from "../components/hs/PdfDownloadButton";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { toast } from "sonner";
import {
  ArrowLeft, Plus, Trash, Receipt, FilePdf, Briefcase, Folder,
  Copy, PencilSimple, CheckCircle, Warning, Clock, UploadSimple, EnvelopeSimple, ArrowsClockwise, LinkSimple, PaperPlaneTilt, BellRinging
} from "@phosphor-icons/react";
import { CreditCard, CurrencyGbp, Recycle } from "@phosphor-icons/react";
import { useAuth } from "../contexts/AuthContext";
import { PopsItemsPicker } from "../components/pops/PopsItemsPicker";
import { PopsInvoiceCard } from "../components/pops/PopsInvoiceCard";
import { hasPops } from "../bespoke/PopsWasteList/popsApi";
import InvoiceDirectDebitButton from "../components/InvoiceDirectDebitButton";
import AddressAutocomplete from "../components/AddressAutocomplete";
import ReverseCalcDialog from "../components/ReverseCalcDialog";
import { PriceBookPicker } from "../components/pricebooks/PriceBookPicker";
import { fmtDate } from "../lib/dateUtil";

const PROPERTY_STATUSES = [
  { value: "tenanted", label: "Tenanted" },
  { value: "void", label: "Void" },
  { value: "vacant", label: "Vacant" },
  { value: "occupied", label: "Occupied" },
  { value: "under_renovation", label: "Under Renovation" },
  { value: "hmo", label: "HMO" },
  { value: "commercial", label: "Commercial" },
];

const STATUS_STYLES = {
  draft: "bg-slate-100 text-slate-700", sent: "bg-blue-100 text-blue-700",
  paid: "bg-emerald-100 text-emerald-700", overdue: "bg-red-100 text-red-700",
  cancelled: "bg-slate-200 text-slate-600",
  expired: "bg-amber-100 text-amber-700", converted: "bg-violet-100 text-violet-700",
};

const EMPTY_LINE = { item_name: "", description: "", quantity: 1, unit_cost: 0 };

export default function InvoiceDetailPage() {
  const { invoiceId } = useParams();
  const navigate = useNavigate();
  const [urlParams] = useSearchParams();
  const isNew = invoiceId === "new";

  const [invoice, setQuote] = useState(null);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [reverseCalcOpen, setReverseCalcOpen] = useState(false);
  const [pbInfo, setPbInfo] = useState(null);
  const [clients, setClients] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [markupTemplates, setMarkupTemplates] = useState([]);
  const [quoteTemplates, setQuoteTemplates] = useState([]);
  const [showConvert, setShowConvert] = useState(false);
  const [converting, setConverting] = useState(false);


  // Email Invoice (opt-in feature)
  const [emailEnabled, setEmailEnabled] = useState(false);
  const [showEmailDialog, setShowEmailDialog] = useState(false);
  const [emailForm, setEmailForm] = useState({ to_email: "", cc_emails: "", subject: "", message: "", branding: {} });
  const [emailSending, setEmailSending] = useState(false);
  const [emailInfo, setEmailInfo] = useState({ send_count: 0, last_sent_at: "" });

  // Form state
  const [form, setForm] = useState({
    title: "", client_id: "", client_name: "", client_email: "", client_phone: "", client_company: "",
    contact_id: "", contact_name: "", contact_color: "",
    property_name: "", street_address: "", town_city: "", county: "", postcode: "",
    property_status: "", tenant_name: "", tenant_phone: "",
    site_contact_name: "", site_contact_phone: "",
    markup_a_pct: 0, markup_b_pct: 0, markup_a_label: "Our Mark Up", markup_b_label: "Client Mark Up",
    line_items: [{ ...EMPTY_LINE, id: crypto.randomUUID() }],
    due_days: 30, terms: "", notes: "",
    vat_enabled: false, vat_rate: 0,
    bank_account_name: "", bank_sort_code: "", bank_account_number: "", bank_name: "", bank_reference_label: "",
    payment_instructions: "",
    job_id: "", job_reference: "",
    emergency_job_id: "", emergency_reference: "", project_id: "", project_reference: "", source_type: "",
    contract_id: "", contract_reference: "", visit_ids: [], client_po_number: "", po_required: false, po_required_by: "", po_waived: false,
    images: [],
  });
  // Send to client portal
  const [showPortalDialog, setShowPortalDialog] = useState(false);
  const [portalMsg, setPortalMsg] = useState("");
  const [portalAttach, setPortalAttach] = useState(true);
  const [portalSending, setPortalSending] = useState(false);
  const [portalUsers, setPortalUsers] = useState(null);
  const prefillDoneRef = useRef(null);

  const fetchQuote = useCallback(async () => {
    if (isNew) return;
    setLoading(true);
    try {
      const { data } = await API.get(`/invoices/${invoiceId}`);
      setQuote(data);
      setForm({
        title: data.title || "", client_id: data.client_id || "", client_name: data.client_name || "",
        client_email: data.client_email || "", client_phone: data.client_phone || "",
        client_company: data.client_company || "",
        contact_id: data.contact_id || "", contact_name: data.contact_name || "", contact_color: data.contact_color || "",
        property_name: data.property_name || "", street_address: data.street_address || "",
        town_city: data.town_city || "", county: data.county || "", postcode: data.postcode || "",
        property_status: data.property_status || "", tenant_name: data.tenant_name || "", tenant_phone: data.tenant_phone || "",
        site_contact_name: data.site_contact_name || "", site_contact_phone: data.site_contact_phone || "",
        markup_a_pct: data.markup_a_pct || 0, markup_b_pct: data.markup_b_pct || 0,
        markup_a_label: data.markup_a_label || "Our Mark Up", markup_b_label: data.markup_b_label || "Client Mark Up",
        line_items: data.line_items?.length ? data.line_items : [{ ...EMPTY_LINE, id: crypto.randomUUID() }],
        due_days: data.due_days || 30, terms: data.terms || "", notes: data.notes || "",
        vat_enabled: data.vat_enabled || false, vat_rate: data.vat_rate || 0,
        bank_account_name: data.bank_account_name || "", bank_sort_code: data.bank_sort_code || "",
        bank_account_number: data.bank_account_number || "", bank_name: data.bank_name || "",
        bank_reference_label: data.bank_reference_label || "",
        payment_instructions: data.payment_instructions || "",
        job_id: data.job_id || "",
        job_reference: data.job_reference || "",
        emergency_job_id: data.emergency_job_id || "", emergency_reference: data.emergency_reference || "",
        project_id: data.project_id || "", project_reference: data.project_reference || "", source_type: data.source_type || "",
        contract_id: data.contract_id || "", contract_reference: data.contract_reference || "", visit_ids: data.visit_ids || [], client_po_number: data.client_po_number || "",
        images: data.images || [],
      });
      // Load contacts for this client
      if (data.client_id) {
        try {
          const cr = await API.get(`/clients/${data.client_id}`);
          setContacts(cr.data.contacts || []);
        } catch {}
      }
    } catch { toast.error("Failed to load invoice"); navigate("/invoices"); }
    finally { setLoading(false); }
  }, [invoiceId, isNew, navigate]);

  useEffect(() => { fetchQuote(); }, [fetchQuote]);

  useEffect(() => {
    API.get("/clients?limit=500").then(({ data }) => setClients(data.clients || [])).catch(() => {});
    API.get("/markup-templates").then(({ data }) => setMarkupTemplates(data.templates || [])).catch(() => {});
    API.get("/invoice-templates").then(({ data }) => setQuoteTemplates(data.templates || [])).catch(() => {});
    // Load invoice-email feature flag (white-label opt-in)
    API.get("/settings/invoice-email").then(({ data }) => setEmailEnabled(!!data.enabled)).catch(() => {});
    // Auto-load VAT settings for new invoices
    if (isNew) {
      API.get("/settings/vat").then(({ data }) => {
        if (data.vat_enabled) {
          setForm(f => ({ ...f, vat_enabled: true, vat_rate: data.vat_rate || 20 }));
        }
      }).catch(() => {});
      // Auto-load bank details defaults for new invoices
      API.get("/settings/bank-details").then(({ data }) => {
        setForm(f => ({
          ...f,
          bank_account_name: data.account_name || "",
          bank_sort_code: data.sort_code || "",
          bank_account_number: data.account_number || "",
          bank_name: data.bank_name || "",
          bank_reference_label: data.reference_label || "",
          payment_instructions: data.payment_instructions || "",
        }));
      }).catch(() => {});

      // Auto-link to a source when arrived from a Job / Emergency / Project "Raise Invoice" CTA
      const prefillJobId = urlParams.get("job_id");
      const prefillEmergencyId = urlParams.get("emergency_id");
      const prefillProjectId = urlParams.get("project_id");
      const prefillContractId = urlParams.get("contract_id");
      const prefillUrl = prefillJobId ? `/invoices/job-prefill/${prefillJobId}` : prefillEmergencyId ? `/invoices/emergency-prefill/${prefillEmergencyId}` : prefillProjectId ? `/invoices/project-prefill/${prefillProjectId}` : prefillContractId ? `/invoices/contract-prefill/${prefillContractId}?visit_ids=${urlParams.get("visit_ids") || ""}` : null;
      if (prefillUrl && prefillDoneRef.current !== prefillUrl) {
        prefillDoneRef.current = prefillUrl;
        API.get(prefillUrl).then(({ data }) => {
          setForm(f => ({
            ...f,
            job_id: data.job_id || "",
            job_reference: data.job_reference || "",
            emergency_job_id: data.emergency_job_id || "", emergency_reference: data.emergency_reference || "",
            project_id: data.project_id || "", project_reference: data.project_reference || "",
            contract_id: data.contract_id || "", contract_reference: data.contract_reference || "", visit_ids: data.visit_ids || [],
            client_po_number: data.client_po_number || f.client_po_number,
            po_required: !!data.po_required, po_required_by: data.po_required_by || "", po_waived: !!data.po_waived,
            source_type: data.source_type || (data.job_id ? "job" : ""),
            contact_id: data.contact_id || f.contact_id, contact_name: data.contact_name || f.contact_name, contact_color: data.contact_color || f.contact_color,
            notes: f.notes?.trim() ? f.notes : (data.notes || f.notes),
            title: f.title?.trim() ? f.title : data.title,
            client_id: data.client_id || f.client_id,
            client_name: data.client_name || f.client_name,
            client_email: data.client_email || f.client_email,
            client_phone: data.client_phone || f.client_phone,
            client_company: data.client_company || f.client_company,
            property_name: data.property_name || f.property_name,
            street_address: data.street_address || f.street_address,
            town_city: data.town_city || f.town_city,
            county: data.county || f.county,
            postcode: data.postcode || f.postcode,
            site_contact_name: data.site_contact_name || f.site_contact_name,
            site_contact_phone: data.site_contact_phone || f.site_contact_phone,
            markup_a_pct: data.markup_a_pct || f.markup_a_pct,
            markup_b_pct: data.markup_b_pct || f.markup_b_pct,
            markup_a_label: data.markup_a_label || f.markup_a_label,
            markup_b_label: data.markup_b_label || f.markup_b_label,
            line_items: (data.line_items?.length && (data.line_items[0].unit_cost > 0 || data.line_items[0].item_name))
              ? data.line_items
              : f.line_items,
          }));
          if (data.client_id) API.get(`/clients/${data.client_id}`).then((cr) => setContacts(cr.data.contacts || [])).catch(() => {});
          const cnt = (data.line_items || []).filter(li => li.unit_cost > 0).length;
          const ref = data.job_reference || data.emergency_reference || data.project_reference || data.contract_reference;
          const srcLabel = data.emergency_job_id ? "emergency job" : data.project_id ? "project" : data.contract_id ? "contract" : "job";
          const how = data.line_source === "approved_quote" ? " from the approved quote" : data.line_source === "tasks" ? " from tasks" : data.line_source === "visits" ? ` from ${data.visit_count} visit${data.visit_count !== 1 ? "s" : ""}` : data.line_source === "price_book" ? ` from the ${data.price_book?.book_name || "price book"} rate card (${data.price_book?.band_label || ""})` : "";
          if (data.line_source === "price_book" && data.price_book) setPbInfo(data.price_book);
          if (data.line_source === "sub_jobs") {
            toast.success(`Linked main job ${ref} — one line per sub-job (${data.sub_jobs_completed} of ${data.sub_job_count} complete). Add prices to each line.`);
            return;
          }
          toast.success(
            cnt > 0
              ? `Linked ${srcLabel} ${ref} — ${cnt} line item${cnt !== 1 ? "s" : ""} pulled in${how}`
              : `Linked ${srcLabel} ${ref} — no costs recorded yet, add your line items`
          );
        }).catch((e) => {
          toast.error(e.response?.data?.detail || "Could not link the source");
        });
      }
    }
  }, [isNew]);

  const handleClientSelect = async (clientId) => {
    const client = clients.find(c => c.id === clientId);
    if (!client) return;
    setForm(f => ({
      ...f,
      client_id: client.id,
      client_name: `${client.first_name} ${client.last_name}`.trim(),
      client_email: client.email || "",
      client_phone: client.phone || "",
      client_company: client.company_name || "",
    }));
    setContacts(client.contacts || []);
  };

  // ── Job linker ─────────────────────────────────────────────
  const [jobSearch, setJobSearch] = useState("");
  const [jobResults, setJobResults] = useState([]);
  const [jobMenuOpen, setJobMenuOpen] = useState(false);
  const [jobLoading, setJobLoading] = useState(false);

  useEffect(() => {
    if (!jobMenuOpen) return;
    let cancelled = false;
    const timer = setTimeout(async () => {
      setJobLoading(true);
      try {
        const params = { limit: 25 };
        if (jobSearch.trim()) params.search = jobSearch.trim();
        const { data } = await API.get("/jobs", { params });
        if (!cancelled) setJobResults(data.jobs || []);
      } catch { if (!cancelled) setJobResults([]); }
      finally { if (!cancelled) setJobLoading(false); }
    }, 200);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [jobSearch, jobMenuOpen]);

  const handleLinkJob = async (jobId) => {
    try {
      const { data } = await API.get(`/invoices/job-prefill/${jobId}`);
      setForm(f => ({
        ...f,
        // Source linkage
        job_id: data.job_id,
        job_reference: data.job_reference,
        // Title (only if user hasn't typed one yet)
        title: f.title?.trim() ? f.title : data.title,
        // Client + property
        client_id: data.client_id || f.client_id,
        client_name: data.client_name || f.client_name,
        client_email: data.client_email || f.client_email,
        client_phone: data.client_phone || f.client_phone,
        client_company: data.client_company || f.client_company,
        property_name: data.property_name || f.property_name,
        street_address: data.street_address || f.street_address,
        town_city: data.town_city || f.town_city,
        county: data.county || f.county,
        postcode: data.postcode || f.postcode,
        site_contact_name: data.site_contact_name || f.site_contact_name,
        site_contact_phone: data.site_contact_phone || f.site_contact_phone,
        // Markup defaults from selected supplier quote
        markup_a_pct: data.markup_a_pct || f.markup_a_pct,
        markup_b_pct: data.markup_b_pct || f.markup_b_pct,
        markup_a_label: data.markup_a_label || f.markup_a_label,
        markup_b_label: data.markup_b_label || f.markup_b_label,
        // Replace line items only if the prefill brought meaningful ones
        line_items: (data.line_items?.length && (data.line_items[0].unit_cost > 0 || data.line_items[0].item_name))
          ? data.line_items
          : f.line_items,
      }));
      setJobMenuOpen(false);
      setJobSearch("");
      const cnt = (data.line_items || []).filter(li => li.unit_cost > 0).length;
      toast.success(
        cnt > 0
          ? `Linked job ${data.job_reference} — ${cnt} line item${cnt !== 1 ? "s" : ""} pulled in`
          : `Linked job ${data.job_reference}`
      );
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to link job");
    }
  };

  const handleUnlinkJob = () => {
    setForm(f => ({ ...f, job_id: "", job_reference: "", source_type: f.source_type === "job" ? "" : f.source_type }));
    toast.success("Job unlinked");
  };
  const handleUnlinkSource = () => {
    setForm(f => ({ ...f, emergency_job_id: "", emergency_reference: "", project_id: "", project_reference: "", contract_id: "", contract_reference: "", visit_ids: [], source_type: f.job_id ? "job" : "" }));
    toast.success("Source unlinked");
  };

  const openPortalDialog = async () => {
    if (!invoice?.client_id) { toast.error("Select a client on the invoice first — the portal is per client"); return; }
    setPortalMsg(""); setPortalAttach(true); setPortalUsers(null);
    setShowPortalDialog(true);
    try {
      const { data } = await API.get(`/clients/${invoice.client_id}/portal-users`);
      const list = Array.isArray(data) ? data : (data.users || []);
      setPortalUsers(list.filter(u => u.active !== false));
    } catch { setPortalUsers([]); }
  };
  const [reminding, setReminding] = useState(false);
  const handleRemind = async () => {
    setReminding(true);
    try {
      const { data } = await API.post(`/invoices/${invoiceId}/remind`);
      toast.success(data.notified > 0 ? `${data.kind === "overdue" ? "Overdue" : "Due-soon"} reminder sent to ${data.notified} portal user${data.notified !== 1 ? "s" : ""}` : "Reminder recorded — no active portal users to notify");
      fetchQuote();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not send reminder"); }
    finally { setReminding(false); }
  };
  const handleSendToPortal = async () => {
    setPortalSending(true);
    try {
      const { data } = await API.post(`/invoices/${invoiceId}/send-to-portal`, { message: portalMsg, attach_pdf: portalAttach });
      toast.success(data.notified > 0 ? `Sent to ${data.client_name}'s portal — ${data.notified} user${data.notified !== 1 ? "s" : ""} notified` : `Published to ${data.client_name}'s portal (no active portal users to notify)`);
      setShowPortalDialog(false);
      fetchQuote();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not send to portal"); }
    finally { setPortalSending(false); }
  };

  const handleContactSelect = (contactId) => {
    const contact = contacts.find(c => c.id === contactId);
    if (!contact) return;
    setForm(f => ({
      ...f,
      contact_id: contact.id,
      contact_name: `${contact.first_name} ${contact.last_name}`.trim(),
      contact_color: contact.color || "",
    }));
  };

  const handleMarkupTemplate = (templateId) => {
    const tpl = markupTemplates.find(t => t.id === templateId);
    if (!tpl) return;
    setForm(f => ({
      ...f,
      markup_a_pct: tpl.markup_a_pct, markup_b_pct: tpl.markup_b_pct,
      markup_a_label: tpl.markup_a_label, markup_b_label: tpl.markup_b_label,
    }));
  };

  const handleLoadQuoteTemplate = (templateId) => {
    const tpl = quoteTemplates.find(t => t.id === templateId);
    if (!tpl) return;
    const items = (tpl.line_items || []).map(li => ({ ...li, id: crypto.randomUUID() }));
    setForm(f => ({
      ...f,
      line_items: items.length ? items : f.line_items,
      terms: tpl.terms || f.terms,
      due_days: tpl.due_days || tpl.due_days || f.due_days || 30,
      markup_a_pct: tpl.markup_a_pct || f.markup_a_pct,
      markup_b_pct: tpl.markup_b_pct || f.markup_b_pct,
      markup_a_label: tpl.markup_a_label || f.markup_a_label,
      markup_b_label: tpl.markup_b_label || f.markup_b_label,
    }));
    toast.success(`Loaded template: ${tpl.name}`);
  };

  const handleSaveAsTemplate = async () => {
    const name = window.prompt("Template name:", form.title || "My Template");
    if (!name) return;
    try {
      await API.post("/invoice-templates", {
        name, line_items: form.line_items, terms: form.terms,
        due_days: form.due_days, markup_a_pct: form.markup_a_pct,
        markup_b_pct: form.markup_b_pct, markup_a_label: form.markup_a_label,
        markup_b_label: form.markup_b_label,
      });
      toast.success(`Template "${name}" saved`);
      API.get("/invoice-templates").then(({ data }) => setQuoteTemplates(data.templates || [])).catch(() => {});
    } catch { toast.error("Failed to save template"); }
  };

  const imageInputRef = useRef(null);
  const [uploadingImage, setUploadingImage] = useState(false);

  const handleImageUpload = async (files) => {
    if (!files?.length) return;
    setUploadingImage(true);
    try {
      const newImages = [];
      for (const file of Array.from(files)) {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await API.post("/upload?entity_type=quote_image&entity_id=" + (invoiceId || "new"), fd, { headers: { "Content-Type": "multipart/form-data" } });
        newImages.push({
          id: crypto.randomUUID(),
          file_id: data.file_id || data.id,
          file_name: data.file_name || file.name,
          file_url: data.file_url || `/api/files/${data.file_id}/${data.file_name}`,
          label: "",
          linked_item: "",
        });
      }
      setForm(f => ({ ...f, images: [...f.images, ...newImages] }));
      toast.success(`${newImages.length} image(s) uploaded`);
    } catch { toast.error("Failed to upload images"); }
    finally { setUploadingImage(false); }
  };

  const updateImage = (idx, field, value) => {
    setForm(f => {
      const imgs = [...f.images];
      imgs[idx] = { ...imgs[idx], [field]: value };
      return { ...f, images: imgs };
    });
  };

  const removeImage = (idx) => setForm(f => ({ ...f, images: f.images.filter((_, i) => i !== idx) }));

  // Line item helpers
  const updateLine = (idx, field, value) => {
    setForm(f => {
      const items = [...f.line_items];
      items[idx] = { ...items[idx], [field]: value };
      return { ...f, line_items: items };
    });
  };
  const addLine = () => setForm(f => ({ ...f, line_items: [...f.line_items, { ...EMPTY_LINE, id: crypto.randomUUID() }] }));
  const [pbOpen, setPbOpen] = useState(false);
  const addPriceBookLines = (rows) => setForm(f => ({ ...f, line_items: [...f.line_items.filter(li => li.item_name || li.description || Number(li.unit_cost) > 0), ...rows] }));
  const { user: authUser } = useAuth();
  const popsEnabled = hasPops(authUser);
  const [popsOpen, setPopsOpen] = useState(false);
  const applyRateCard = async () => {
    try {
      const { data } = await API.get(`/price-books/job-lines/${form.job_id}`);
      if (!data.book_id) { toast.error(data.reason || "No price book applies to this client"); return; }
      if (!data.lines.length) { toast.info(`${data.book_name}: no time or parts recorded on this job yet — add lines from the price book instead`); return; }
      addPriceBookLines(data.lines);
      setPbInfo(data);
      toast.success(`${data.lines.length} line${data.lines.length === 1 ? "" : "s"} priced from ${data.book_name} (${data.band_label}${data.minutes ? ` · ${data.minutes} min on site` : ""})`);
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't apply the rate card"); }
  };
  const removeLine = (idx) => setForm(f => ({ ...f, line_items: f.line_items.filter((_, i) => i !== idx) }));

  // Calculate totals
  const calcLine = (line) => {
    const base = (parseFloat(line.quantity) || 0) * (parseFloat(line.unit_cost) || 0);
    const afterA = base * (1 + (parseFloat(form.markup_a_pct) || 0) / 100);
    const total = afterA * (1 + (parseFloat(form.markup_b_pct) || 0) / 100);
    return { base: Math.round(base * 100) / 100, afterA: Math.round(afterA * 100) / 100, total: Math.round(total * 100) / 100 };
  };
  const baseTotal = form.line_items.reduce((s, li) => s + calcLine(li).base, 0);
  const afterATotal = form.line_items.reduce((s, li) => s + calcLine(li).afterA, 0);
  const grandTotal = form.line_items.reduce((s, li) => s + calcLine(li).total, 0);
  const vatAmount = form.vat_enabled && form.vat_rate > 0 ? Math.round(grandTotal * (form.vat_rate / 100) * 100) / 100 : 0;
  const totalIncVat = Math.round((grandTotal + vatAmount) * 100) / 100;

  const handleSave = async () => {
    if (!form.title.trim()) { toast.error("Title is required"); return; }
    if (form.line_items.length === 0) { toast.error("Add at least one line item"); return; }
    setSaving(true);
    try {
      if (isNew) {
        const { data } = await API.post("/invoices", form);
        toast.success(`Invoice ${data.reference} created`);
        navigate(`/invoices/${data.id}`);
      } else {
        await API.put(`/invoices/${invoiceId}`, form);
        toast.success("Invoice updated");
        fetchQuote();
      }
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to save"); }
    finally { setSaving(false); }
  };

  const handleStatusChange = async (status) => {
    try {
      await API.put(`/invoices/${invoiceId}/status`, { status });
      toast.success(`Status updated to ${status}`);
      fetchQuote();
    } catch { toast.error("Failed to update status"); }
  };

  const handleOpenPayPage = async () => {
    try {
      const { data } = await API.post(`/invoices/${invoiceId}/payment-link`);
      window.open(`${window.location.origin}${data.pay_path}`, "_blank", "noopener");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not open the pay page"); }
  };

  const handleCopyPayLink = async () => {
    try {
      const { data } = await API.post(`/invoices/${invoiceId}/payment-link`);
      const url = `${window.location.origin}${data.pay_path}`;
      try {
        await navigator.clipboard.writeText(url);
        toast.success("Payment link copied — send it to your client to pay by card");
      } catch {
        window.prompt("Copy this payment link:", url);
        toast.success("Payment link generated");
      }
    } catch (e) { toast.error(e.response?.data?.detail || "Could not create payment link"); }
  };

  const handleConvert = async (type) => {
    setConverting(true);
    try {
      const endpoint = type === "job" ? `/invoices/${invoiceId}/convert-to-job` : `/invoices/${invoiceId}/convert-to-project`;
      const { data } = await API.post(endpoint);
      toast.success(data.message);
      setShowConvert(false);
      fetchQuote();
      if (type === "job") navigate(`/jobs/${data.job_id}`);
      else navigate(`/projects/${data.project_id}`);
    } catch (err) { toast.error(err.response?.data?.detail || "Conversion failed"); }
    finally { setConverting(false); }
  };

  const handleDownloadPdf = async (view) => {
    try {
      const response = await API.get(`/invoices/${invoiceId}/pdf?view=${view}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: "application/pdf" }));
      const link = document.createElement("a");
      link.href = url;
      link.download = `${invoice?.reference || "invoice"}_${view}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`${view === "client" ? "Client" : "Admin"} PDF downloaded`);
    } catch { toast.error("Failed to download PDF"); }
  };

  const openEmailDialog = async () => {
    try {
      const { data } = await API.get(`/invoices/${invoiceId}/email-defaults`);
      setEmailForm({
        to_email: data.to_email || "",
        cc_emails: "",
        subject: data.subject || "",
        message: data.message || "",
        branding: data.branding || {},
      });
      setEmailInfo({ send_count: data.send_count || 0, last_sent_at: data.last_sent_at || "" });
      setShowEmailDialog(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Email-from-platform is not enabled. Turn it on in Settings > Invoice Email.");
    }
  };

  const handleSendEmail = async () => {
    if (!emailForm.to_email || !emailForm.to_email.includes("@")) {
      toast.error("Please enter a valid recipient email");
      return;
    }
    setEmailSending(true);
    try {
      await API.post(`/invoices/${invoiceId}/send-email`, {
        to_email: emailForm.to_email,
        cc_emails: emailForm.cc_emails,
        subject: emailForm.subject,
        message: emailForm.message,
        branding: emailForm.branding || {},
      });
      toast.success(`Invoice emailed to ${emailForm.to_email}`);
      setShowEmailDialog(false);
      fetchQuote();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send email");
    } finally {
      setEmailSending(false);
    }
  };

  if (loading) return <div className="flex justify-center py-16"><div className="animate-spin h-6 w-6 border-2 border-primary border-t-transparent rounded-full" /></div>;

  return (
    <div data-testid="invoice-detail-page">
      <div className="flex items-center gap-2 mb-4">
        <Link to="/invoices" className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1">
          <ArrowLeft size={14} /> Back to Invoices
        </Link>
        {invoice && <Badge className={`ml-2 text-[10px] ${STATUS_STYLES[invoice.status] || ""}`}>{invoice.status?.toUpperCase()}</Badge>}
        {invoice?.portal_sent_at && (
          <span className="ml-1 inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300 font-semibold" title={`Sent to client portal ${new Date(invoice.portal_sent_at).toLocaleString("en-GB")} by ${invoice.portal_sent_by_name || ""}`} data-testid="in-portal-badge">
            <PaperPlaneTilt size={11} weight="fill" /> In client portal{invoice.portal_send_count > 1 ? ` ×${invoice.portal_send_count}` : ""}
          </span>
        )}
        {invoice?.portal_reminders && (invoice.portal_reminders.due_soon || invoice.portal_reminders.overdue || invoice.portal_reminders.manual) && (
          <span className="ml-1 inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300 font-semibold" data-testid="reminders-badge"
            title={[invoice.portal_reminders.due_soon && `Due-soon reminder ${new Date(invoice.portal_reminders.due_soon).toLocaleDateString("en-GB")}`, invoice.portal_reminders.overdue && `Overdue reminder ${new Date(invoice.portal_reminders.overdue).toLocaleDateString("en-GB")}`, invoice.portal_reminders.manual && `Manual reminder ${new Date(invoice.portal_reminders.manual).toLocaleDateString("en-GB")}`].filter(Boolean).join(" · ")}>
            <BellRinging size={11} weight="fill" /> {invoice.portal_reminder_count || 1} reminder{(invoice.portal_reminder_count || 1) !== 1 ? "s" : ""} sent
          </span>
        )}
        {invoice && (invoice.contract_reference || invoice.job_reference || invoice.emergency_reference || invoice.project_reference) && (
          <Link to={invoice.contract_id ? `/contracts/${invoice.contract_id}` : invoice.job_id ? `/jobs/${invoice.job_id}` : invoice.emergency_job_id ? `/emergency-jobs/${invoice.emergency_job_id}` : `/projects/${invoice.project_id}`} className="ml-1 text-[10px] px-2 py-0.5 rounded-full bg-muted text-muted-foreground hover:text-foreground font-mono" data-testid="invoice-source-chip">
            {invoice.contract_reference || invoice.job_reference || invoice.emergency_reference || invoice.project_reference}
          </Link>
        )}
        {invoice?.client_po_number && <span className="ml-1 text-[10px] px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300 font-semibold" data-testid="invoice-po-chip">PO {invoice.client_po_number}</span>}
      </div>

      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="h-9 w-9 rounded-lg bg-gradient-to-br from-teal-500 to-cyan-600 flex items-center justify-center">
            <Receipt size={18} className="text-white" weight="fill" />
          </div>
          <div>
            <h1 className="text-xl font-bold font-[Chivo] tracking-tight">{isNew ? "New Invoice" : invoice?.reference}</h1>
            {invoice && <p className="text-xs text-muted-foreground">{fmtDate(invoice.created_at)}</p>}
          </div>
        </div>
        {!isNew && invoice?.status !== "converted" && (
          <div className="flex items-center gap-2">
            {invoice?.status === "draft" && <Button size="sm" variant="outline" onClick={() => handleStatusChange("sent")} data-testid="mark-sent-btn">Mark as Sent</Button>}
            {invoice?.status === "sent" && (
              <>
                <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => handleStatusChange("paid")} data-testid="mark-paid-btn">Mark Paid</Button>
                <Button size="sm" variant="outline" className="text-red-600 border-red-200" onClick={() => handleStatusChange("overdue")} data-testid="mark-overdue-btn">Mark Overdue</Button>
              </>
            )}
            {invoice?.status === "overdue" && (
              <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => handleStatusChange("paid")} data-testid="mark-paid-btn">Mark Paid</Button>
            )}
            <PdfDownloadButton url={`/invoices/${invoiceId}/pdf?view=client`} label="Client PDF" testid="pdf-client-btn" clientId={form.client_id} />
            <Button size="sm" variant="outline" onClick={() => handleDownloadPdf("admin")} data-testid="pdf-admin-btn">
              <FilePdf size={14} className="mr-1" /> Admin PDF
            </Button>
            {invoice?.status !== "paid" && invoice?.status !== "cancelled" && (<>
              <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={handleOpenPayPage} data-testid="open-pay-page-btn" title="Open the public card-payment page for this invoice"><CreditCard size={14} className="mr-1" /> Pay online</Button>
              <Button size="sm" variant="outline" className="text-emerald-700 border-emerald-300 hover:bg-emerald-50 dark:text-emerald-400" onClick={handleCopyPayLink} data-testid="copy-pay-link-btn" title="Copy a public Stripe payment link for this invoice">
                <LinkSimple size={14} className="mr-1" /> Pay Link
              </Button>
            </>)}
            {invoice && <InvoiceDirectDebitButton invoice={invoice} onChanged={fetchQuote} />}
            {emailEnabled && (
              <Button size="sm" className="bg-blue-600 hover:bg-blue-700 text-white" onClick={openEmailDialog} data-testid="email-invoice-btn">
                <EnvelopeSimple size={14} className="mr-1" /> Email Invoice
              </Button>
            )}
            {["sent", "overdue"].includes(invoice?.status) && invoice?.client_id && (
              <Button size="sm" variant="outline" onClick={handleRemind} disabled={reminding} data-testid="send-reminder-btn" title="Send a payment reminder to the client's portal users now">
                <BellRinging size={14} className="mr-1" /> {reminding ? "Sending…" : "Send Reminder"}
              </Button>
            )}
            {invoice?.status !== "cancelled" && (
              <Button size="sm" className="bg-primary text-primary-foreground" onClick={openPortalDialog} data-testid="send-to-portal-btn" title="Publish to the client's portal and notify their users">
                <PaperPlaneTilt size={14} className="mr-1" /> {invoice?.portal_sent_at ? "Resend to Portal" : "Send to Client Portal"}
              </Button>
            )}
          </div>
        )}
        {/* Removed: converted-to-job block (quote-specific) */}
      </div>

      {/* Form */}
      <div className="space-y-6">
        {!isNew && popsEnabled && <PopsInvoiceCard invoiceId={invoiceId} user={authUser} refreshKey={invoice?.updated_at} />}
        {/* Title */}
        <div>
          <Label className="text-xs font-semibold">Invoice Title *</Label>
          <Input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} placeholder="e.g. Kitchen Renovation - 12 High St" className="mt-1" data-testid="invoice-title" />
        </div>

        {/* Link to Job (optional) */}
        <div className="border border-border rounded-lg p-4 space-y-3" data-testid="invoice-job-linker">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div>
              <h3 className="text-sm font-semibold font-[Chivo] flex items-center gap-2">
                <Briefcase size={14} /> Link to Job <span className="text-[10px] text-muted-foreground font-normal">(optional)</span>
              </h3>
              <p className="text-[11px] text-muted-foreground">Pulls in the client, property, markup, and the selected supplier quote's line items. Everything stays editable.</p>
            </div>
            {form.job_id && (
              <div className="flex items-center gap-2 bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800/50 rounded-md px-2.5 py-1">
                <CheckCircle size={14} weight="fill" className="text-emerald-600 dark:text-emerald-400" />
                <Link to={`/jobs/${form.job_id}`} className="text-xs font-mono font-semibold text-emerald-800 dark:text-emerald-300 hover:underline" data-testid="linked-job-ref">{form.job_reference || "Linked"}</Link>
                <button type="button" onClick={handleUnlinkJob} className="text-[10px] text-rose-600 hover:underline ml-1" data-testid="unlink-job-btn">unlink</button>
              </div>
            )}
            {(form.emergency_job_id || form.project_id || form.contract_id) && (
              <div className="flex items-center gap-2 bg-violet-50 dark:bg-violet-900/20 border border-violet-200 dark:border-violet-800/50 rounded-md px-2.5 py-1" data-testid="linked-source-chip">
                <CheckCircle size={14} weight="fill" className="text-violet-600 dark:text-violet-400" />
                <span className="text-[10px] uppercase tracking-wide text-violet-700 dark:text-violet-300 font-semibold">{form.emergency_job_id ? "Emergency" : form.contract_id ? `Contract${form.visit_ids?.length ? ` · ${form.visit_ids.length} visit${form.visit_ids.length !== 1 ? "s" : ""}` : ""}` : "Project"}</span>
                <Link to={form.emergency_job_id ? `/emergency-jobs/${form.emergency_job_id}` : form.contract_id ? `/contracts/${form.contract_id}` : `/projects/${form.project_id}`} className="text-xs font-mono font-semibold text-violet-800 dark:text-violet-300 hover:underline" data-testid="linked-source-ref">{form.emergency_reference || form.contract_reference || form.project_reference || "Linked"}</Link>
                <button type="button" onClick={handleUnlinkSource} className="text-[10px] text-rose-600 hover:underline ml-1" data-testid="unlink-source-btn">unlink</button>
              </div>
            )}
          </div>
          {!form.job_id && !form.emergency_job_id && !form.project_id && !form.contract_id && (
            <div className="relative">
              <Input
                value={jobSearch}
                onFocus={() => setJobMenuOpen(true)}
                onChange={(e) => { setJobSearch(e.target.value); setJobMenuOpen(true); }}
                onBlur={() => setTimeout(() => setJobMenuOpen(false), 180)}
                placeholder="Search by job ref, title, client, or postcode…"
                className="text-sm"
                data-testid="invoice-job-search"
              />
              {jobMenuOpen && (
                <div className="absolute z-20 mt-1 w-full bg-card border border-border rounded-md shadow-lg max-h-72 overflow-auto" data-testid="invoice-job-results">
                  {jobLoading && <div className="px-3 py-2 text-xs text-muted-foreground">Searching…</div>}
                  {!jobLoading && jobResults.length === 0 && (
                    <div className="px-3 py-3 text-xs text-muted-foreground italic text-center">No jobs match. Try a different search term.</div>
                  )}
                  {!jobLoading && jobResults.map(j => (
                    <button
                      key={j.id}
                      type="button"
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => handleLinkJob(j.id)}
                      className="w-full text-left px-3 py-2 hover:bg-muted/50 border-b border-border last:border-0"
                      data-testid={`invoice-job-result-${j.id}`}
                    >
                      <div className="flex items-center justify-between gap-2 flex-wrap">
                        <span className="text-xs font-mono font-semibold">{j.reference}</span>
                        <span className={`text-[9px] px-1.5 py-0.5 rounded-full ${j.status === "completed" ? "bg-emerald-100 text-emerald-700" : j.status === "in_progress" ? "bg-blue-100 text-blue-700" : "bg-slate-100 text-slate-600"}`}>
                          {j.status?.replace(/_/g, " ")}
                        </span>
                      </div>
                      <p className="text-xs font-medium mt-0.5 truncate">{j.title}</p>
                      <p className="text-[10px] text-muted-foreground truncate">
                        {[j.client_name, j.client_company].filter(Boolean).join(" · ")}
                        {j.property_address && <> &middot; {j.property_address}</>}
                      </p>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Client & Contact */}
        <div className="border border-border rounded-lg p-4 space-y-3">
          <h3 className="text-sm font-semibold font-[Chivo]">Client Details</h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <Label className="text-xs">Client *</Label>
              <Select value={form.client_id} onValueChange={handleClientSelect}>
                <SelectTrigger data-testid="invoice-client-select"><SelectValue placeholder="Select client" /></SelectTrigger>
                <SelectContent>
                  {clients.map(c => <SelectItem key={c.id} value={c.id}>{c.first_name} {c.last_name} {c.company_name ? `(${c.company_name})` : ""}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Sub-Contact</Label>
              <Select value={form.contact_id} onValueChange={handleContactSelect}>
                <SelectTrigger data-testid="invoice-contact-select"><SelectValue placeholder="Select contact" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— None —</SelectItem>
                  {contacts.map(c => <SelectItem key={c.id} value={c.id}>{c.first_name} {c.last_name} {c.job_role ? `(${c.job_role})` : ""}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Client Email</Label>
              <Input value={form.client_email} onChange={e => setForm(f => ({ ...f, client_email: e.target.value }))} data-testid="invoice-client-email" />
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            <div><Label className="text-xs">Client Phone</Label><Input value={form.client_phone} onChange={e => setForm(f => ({ ...f, client_phone: e.target.value }))} data-testid="invoice-client-phone" /></div>
            <div><Label className="text-xs">Company</Label><Input value={form.client_company} onChange={e => setForm(f => ({ ...f, client_company: e.target.value }))} data-testid="invoice-client-company" /></div>
            <div>
              <Label className="text-xs">Client PO Number <span className="text-muted-foreground">(printed on the invoice)</span>{form.po_required && <span className="ml-1 text-violet-700 font-semibold" data-testid="invoice-po-required-tag">· required by {form.po_required_by || "client"}</span>}</Label>
              <Input value={form.client_po_number} onChange={e => setForm(f => ({ ...f, client_po_number: e.target.value }))} placeholder={form.po_required ? "Required before this invoice can be created" : "e.g. PO-48213"} className={form.po_required && !form.client_po_number?.trim() && !form.po_waived ? "border-amber-400" : ""} data-testid="invoice-client-po" />
              {form.po_required && !form.client_po_number?.trim() && (
                <label className="mt-1 flex items-center gap-1.5 text-[11px] text-amber-800 dark:text-amber-300 cursor-pointer"><input type="checkbox" checked={!!form.po_waived} onChange={e => setForm(f => ({ ...f, po_waived: e.target.checked, po_waiver_reason: e.target.checked ? "Client doesn't issue PO/WOs" : "" }))} data-testid="invoice-po-waive" /> No PO/WO issued — create the invoice without one</label>
              )}
            </div>
          </div>
        </div>

        {/* Property */}
        <div className="border border-border rounded-lg p-4 space-y-3">
          <h3 className="text-sm font-semibold font-[Chivo]">Property Details</h3>
          <AddressAutocomplete
            label="Search address"
            placeholder="Start typing address or postcode..."
            onSelect={(a) => setForm(f => ({
              ...f,
              street_address: a.street_address || f.street_address,
              town_city: a.town_city || f.town_city,
              county: a.county || f.county,
              postcode: a.postcode || f.postcode,
              latitude: a.latitude || null,
              longitude: a.longitude || null,
            }))}
            data-testid="invoice-address-autocomplete"
          />
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div><Label className="text-xs">Property Name</Label><Input value={form.property_name} onChange={e => setForm(f => ({ ...f, property_name: e.target.value }))} placeholder="e.g. Maple House" data-testid="invoice-prop-name" /></div>
            <div className="sm:col-span-2"><Label className="text-xs">Street Address *</Label><Input value={form.street_address} onChange={e => setForm(f => ({ ...f, street_address: e.target.value }))} data-testid="invoice-street" /></div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div><Label className="text-xs">Town/City *</Label><Input value={form.town_city} onChange={e => setForm(f => ({ ...f, town_city: e.target.value }))} data-testid="invoice-city" /></div>
            <div><Label className="text-xs">County</Label><Input value={form.county} onChange={e => setForm(f => ({ ...f, county: e.target.value }))} data-testid="invoice-county" /></div>
            <div><Label className="text-xs">Postcode *</Label><Input value={form.postcode} onChange={e => setForm(f => ({ ...f, postcode: e.target.value }))} data-testid="invoice-postcode" /></div>
            <div>
              <Label className="text-xs">Property Status</Label>
              <Select value={form.property_status} onValueChange={v => setForm(f => ({ ...f, property_status: v }))}>
                <SelectTrigger data-testid="invoice-prop-status"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>{PROPERTY_STATUSES.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div><Label className="text-xs">Tenant Name</Label><Input value={form.tenant_name} onChange={e => setForm(f => ({ ...f, tenant_name: e.target.value }))} data-testid="invoice-tenant-name" /></div>
            <div><Label className="text-xs">Tenant Phone</Label><Input value={form.tenant_phone} onChange={e => setForm(f => ({ ...f, tenant_phone: e.target.value }))} data-testid="invoice-tenant-phone" /></div>
            <div><Label className="text-xs">Site Contact</Label><Input value={form.site_contact_name} onChange={e => setForm(f => ({ ...f, site_contact_name: e.target.value }))} data-testid="invoice-site-contact" /></div>
            <div><Label className="text-xs">Site Phone</Label><Input value={form.site_contact_phone} onChange={e => setForm(f => ({ ...f, site_contact_phone: e.target.value }))} data-testid="invoice-site-phone" /></div>
          </div>
        </div>

        {/* Markup Settings */}
        <div className="border border-border rounded-lg p-4 space-y-3">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <h3 className="text-sm font-semibold font-[Chivo]">Markup Settings</h3>
            <div className="flex items-center gap-2">
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="h-8 text-xs"
                onClick={() => setReverseCalcOpen(true)}
                data-testid="invoice-reverse-calc-btn"
              >
                <ArrowsClockwise size={12} className="mr-1" /> Reverse calc
              </Button>
              {markupTemplates.length > 0 && (
                <Select onValueChange={handleMarkupTemplate}>
                  <SelectTrigger className="w-48 h-8 text-xs" data-testid="markup-template-select"><SelectValue placeholder="Load template..." /></SelectTrigger>
                  <SelectContent>{markupTemplates.map(t => <SelectItem key={t.id} value={t.id}>{t.name} ({t.markup_a_pct}% / {t.markup_b_pct}%)</SelectItem>)}</SelectContent>
                </Select>
              )}
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div><Label className="text-xs">Markup A Label</Label><Input value={form.markup_a_label} onChange={e => setForm(f => ({ ...f, markup_a_label: e.target.value }))} data-testid="markup-a-label" /></div>
            <div><Label className="text-xs">Markup A %</Label><Input type="number" step="0.1" value={form.markup_a_pct} onChange={e => setForm(f => ({ ...f, markup_a_pct: parseFloat(e.target.value) || 0 }))} data-testid="markup-a-pct" /></div>
            <div><Label className="text-xs">Markup B Label</Label><Input value={form.markup_b_label} onChange={e => setForm(f => ({ ...f, markup_b_label: e.target.value }))} data-testid="markup-b-label" /></div>
            <div><Label className="text-xs">Markup B %</Label><Input type="number" step="0.1" value={form.markup_b_pct} onChange={e => setForm(f => ({ ...f, markup_b_pct: parseFloat(e.target.value) || 0 }))} data-testid="markup-b-pct" /></div>
          </div>
        </div>

        {/* Line Items */}
        <div className="border border-border rounded-lg overflow-hidden">
          <div className="px-4 py-3 bg-muted/30 border-b border-border flex items-center justify-between gap-2 flex-wrap">
            <h3 className="text-sm font-semibold font-[Chivo]">Schedule of Works</h3>
            <div className="flex items-center gap-2">
              {quoteTemplates.length > 0 && (
                <Select onValueChange={handleLoadQuoteTemplate}>
                  <SelectTrigger className="w-44 h-8 text-xs" data-testid="load-invoice-template"><SelectValue placeholder="Load template..." /></SelectTrigger>
                  <SelectContent>{quoteTemplates.map(t => <SelectItem key={t.id} value={t.id}>{t.name} ({(t.line_items||[]).length} items)</SelectItem>)}</SelectContent>
                </Select>
              )}
              <Button size="sm" variant="ghost" className="text-xs h-8" onClick={handleSaveAsTemplate} data-testid="save-as-template-btn">
                Save as Template
              </Button>
              {form.job_id && (
                <Button size="sm" variant="outline" onClick={applyRateCard} data-testid="apply-rate-card-btn" title="Price labour time, call-out and parts used on the linked job from the client's rate card">
                  <CurrencyGbp size={14} className="mr-1" /> Apply rate card
                </Button>
              )}
              <Button size="sm" variant="outline" onClick={() => setPbOpen(true)} data-testid="add-from-price-book-btn">
                <CurrencyGbp size={14} className="mr-1" /> From price book
              </Button>
              {popsEnabled && (
                <Button size="sm" variant="outline" className="border-emerald-300 text-emerald-700 dark:text-emerald-300" onClick={() => setPopsOpen(true)} data-testid="add-pops-items-btn">
                  <Recycle size={14} className="mr-1" /> Add POPs items
                </Button>
              )}
              <Button size="sm" variant="outline" onClick={addLine} data-testid="add-line-btn">
                <Plus size={14} className="mr-1" /> Add Item
              </Button>
            </div>
          </div>
          {pbInfo && <p className="px-4 py-1.5 text-[11px] text-muted-foreground bg-primary/5 border-b border-border" data-testid="rate-card-info">Priced from <span className="font-medium text-foreground">{pbInfo.book_name}</span> · {pbInfo.band_label}{pbInfo.minutes ? ` · ${pbInfo.minutes} min on site` : ""}{pbInfo.parts_count ? ` · ${pbInfo.parts_count} part${pbInfo.parts_count === 1 ? "" : "s"}` : ""}</p>}
          <PriceBookPicker open={pbOpen} clientId={form.client_id} onClose={() => setPbOpen(false)} onAdd={addPriceBookLines} />
          {popsEnabled && <PopsItemsPicker open={popsOpen} onClose={() => setPopsOpen(false)} onAdd={addPriceBookLines} jobId={form.job_id || undefined} />}
          <div className="overflow-x-auto">
            <table className="w-full text-xs" data-testid="line-items-table">
              <thead>
                <tr className="border-b border-border bg-muted/10">
                  <th className="px-3 py-2 text-left font-semibold w-8">#</th>
                  <th className="px-3 py-2 text-left font-semibold w-36">Item</th>
                  <th className="px-3 py-2 text-left font-semibold">Description</th>
                  <th className="px-3 py-2 text-right font-semibold w-16">Qty</th>
                  <th className="px-3 py-2 text-right font-semibold w-24">Unit Cost</th>
                  <th className="px-3 py-2 text-right font-semibold w-24">Sub Total</th>
                  {form.markup_a_pct > 0 && <th className="px-3 py-2 text-right font-semibold w-24">{form.markup_a_label}</th>}
                  <th className="px-3 py-2 text-right font-semibold w-24">Sub Total</th>
                  <th className="px-3 py-2 w-10"></th>
                </tr>
              </thead>
              <tbody>
                {form.line_items.map((line, idx) => {
                  const calc = calcLine(line);
                  return (
                    <tr key={line.id || idx} className="border-b border-border" data-testid={`line-${idx}`}>
                      <td className="px-3 py-2 text-muted-foreground">{idx + 1}</td>
                      <td className="px-3 py-1">
                        <Input value={line.item_name || ""} onChange={e => updateLine(idx, "item_name", e.target.value)} className="h-8 text-xs border-0 shadow-none focus-visible:ring-1" placeholder="Item name" data-testid={`line-item-${idx}`} />
                      </td>
                      <td className="px-3 py-1">
                        <Input value={line.description} onChange={e => updateLine(idx, "description", e.target.value)} className="h-8 text-xs border-0 shadow-none focus-visible:ring-1" placeholder="Description" data-testid={`line-desc-${idx}`} />
                      </td>
                      <td className="px-3 py-1">
                        <input value={line.quantity} onChange={e => updateLine(idx, "quantity", e.target.value)} className="h-8 w-16 text-xs text-right bg-transparent outline-none focus:ring-1 focus:ring-primary rounded px-2" placeholder="1" data-testid={`line-qty-${idx}`} />
                      </td>
                      <td className="px-3 py-1">
                        <input value={line.unit_cost} onChange={e => updateLine(idx, "unit_cost", e.target.value)} className="h-8 w-24 text-xs text-right bg-transparent outline-none focus:ring-1 focus:ring-primary rounded px-2" placeholder="0.00" data-testid={`line-cost-${idx}`} />
                      </td>
                      <td className="px-3 py-2 text-right font-mono">&pound;{calc.base.toLocaleString("en-GB", { minimumFractionDigits: 2 })}</td>
                      {form.markup_a_pct > 0 && <td className="px-3 py-2 text-right font-mono">&pound;{calc.afterA.toLocaleString("en-GB", { minimumFractionDigits: 2 })}</td>}
                      <td className="px-3 py-2 text-right font-mono font-semibold">&pound;{calc.total.toLocaleString("en-GB", { minimumFractionDigits: 2 })}</td>
                      <td className="px-3 py-2">
                        {form.line_items.length > 1 && (
                          <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => removeLine(idx)} data-testid={`remove-line-${idx}`}>
                            <Trash size={12} className="text-muted-foreground" />
                          </Button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="bg-muted/20 border-t-2 border-border">
                  <td colSpan={4}></td>
                  <td className="px-3 py-2.5 text-right text-xs font-semibold">Sub Totals:</td>
                  <td className="px-3 py-2.5 text-right font-mono font-semibold text-sm" data-testid="base-total">&pound;{baseTotal.toLocaleString("en-GB", { minimumFractionDigits: 2 })}</td>
                  {form.markup_a_pct > 0 && <td className="px-3 py-2.5 text-right font-mono font-semibold text-sm" data-testid="after-a-total">&pound;{afterATotal.toLocaleString("en-GB", { minimumFractionDigits: 2 })}</td>}
                  <td className="px-3 py-2.5 text-right font-mono font-semibold text-sm" data-testid="grand-total">&pound;{grandTotal.toLocaleString("en-GB", { minimumFractionDigits: 2 })}</td>
                  <td></td>
                </tr>
                {form.vat_enabled && form.vat_rate > 0 && (
                  <>
                    <tr>
                      <td colSpan={4}></td>
                      <td className="px-3 py-1.5 text-right text-xs text-muted-foreground">VAT ({form.vat_rate}%):</td>
                      <td colSpan={form.markup_a_pct > 0 ? 2 : 1}></td>
                      <td className="px-3 py-1.5 text-right font-mono text-sm" data-testid="vat-amount">&pound;{vatAmount.toLocaleString("en-GB", { minimumFractionDigits: 2 })}</td>
                      <td></td>
                    </tr>
                    <tr className="bg-primary/5">
                      <td colSpan={4}></td>
                      <td className="px-3 py-2.5 text-right text-xs font-bold">Total (inc. VAT):</td>
                      <td colSpan={form.markup_a_pct > 0 ? 2 : 1}></td>
                      <td className="px-3 py-2.5 text-right font-mono font-bold text-base text-primary" data-testid="total-inc-vat">&pound;{totalIncVat.toLocaleString("en-GB", { minimumFractionDigits: 2 })}</td>
                      <td></td>
                    </tr>
                  </>
                )}
              </tfoot>
            </table>
          </div>
        </div>

        {/* Cost Breakdown Box */}
        {(form.markup_a_pct > 0 || form.markup_b_pct > 0 || (form.vat_enabled && form.vat_rate > 0)) && (
          <div className="border border-border rounded-lg overflow-hidden" data-testid="cost-breakdown">
            <div className="px-4 py-3 bg-muted/30 border-b border-border">
              <h3 className="text-sm font-semibold font-[Chivo]">Cost Breakdown</h3>
            </div>
            <div className="p-4">
              <table className="w-full max-w-md text-sm">
                <tbody>
                  <tr className="border-b border-border/50">
                    <td className="py-2.5 text-muted-foreground">Sub Total</td>
                    <td className="py-2.5 text-right font-mono font-medium">&pound;{baseTotal.toLocaleString("en-GB", { minimumFractionDigits: 2 })}</td>
                  </tr>
                  {form.markup_a_pct > 0 && (
                    <>
                      <tr className="border-b border-border/50">
                        <td className="py-2.5 text-muted-foreground">{form.markup_a_label} ({form.markup_a_pct}%)</td>
                        <td className="py-2.5 text-right font-mono font-medium">&pound;{(afterATotal - baseTotal).toLocaleString("en-GB", { minimumFractionDigits: 2 })}</td>
                      </tr>
                      <tr className="border-b border-border/50">
                        <td className="py-2.5 text-muted-foreground">After {form.markup_a_label}</td>
                        <td className="py-2.5 text-right font-mono font-medium">&pound;{afterATotal.toLocaleString("en-GB", { minimumFractionDigits: 2 })}</td>
                      </tr>
                    </>
                  )}
                  {form.markup_b_pct > 0 && (
                    <tr className="border-b border-border/50">
                      <td className="py-2.5 text-muted-foreground">{form.markup_b_label} ({form.markup_b_pct}%)</td>
                      <td className="py-2.5 text-right font-mono font-medium">&pound;{(grandTotal - afterATotal).toLocaleString("en-GB", { minimumFractionDigits: 2 })}</td>
                    </tr>
                  )}
                  <tr className="border-b border-border/50">
                    <td className="py-2.5 font-medium text-foreground">Sub Total (excl. VAT)</td>
                    <td className="py-2.5 text-right font-mono font-semibold">&pound;{grandTotal.toLocaleString("en-GB", { minimumFractionDigits: 2 })}</td>
                  </tr>
                  {form.vat_enabled && form.vat_rate > 0 && (
                    <>
                      <tr className="border-b border-border/50">
                        <td className="py-2.5 text-muted-foreground">VAT ({form.vat_rate}%)</td>
                        <td className="py-2.5 text-right font-mono font-medium">&pound;{vatAmount.toLocaleString("en-GB", { minimumFractionDigits: 2 })}</td>
                      </tr>
                      <tr className="bg-primary/5 rounded">
                        <td className="py-3 font-bold text-foreground">Total (inc. VAT)</td>
                        <td className="py-3 text-right font-mono font-bold text-lg text-primary">&pound;{totalIncVat.toLocaleString("en-GB", { minimumFractionDigits: 2 })}</td>
                      </tr>
                    </>
                  )}
                  {!(form.vat_enabled && form.vat_rate > 0) && (
                    <tr className="bg-primary/5 rounded">
                      <td className="py-3 font-semibold text-foreground">Grand Total</td>
                      <td className="py-3 text-right font-mono font-bold text-lg text-primary">&pound;{grandTotal.toLocaleString("en-GB", { minimumFractionDigits: 2 })}</td>
                    </tr>
                  )}
                  <tr>
                    <td className="pt-2 text-xs text-emerald-600 font-medium">Total Profit</td>
                    <td className="pt-2 text-right font-mono text-sm font-semibold text-emerald-600">&pound;{(grandTotal - baseTotal).toLocaleString("en-GB", { minimumFractionDigits: 2 })}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Supporting Images */}
        <div className="border border-border rounded-lg overflow-hidden">
          <div className="px-4 py-3 bg-muted/30 border-b border-border flex items-center justify-between">
            <h3 className="text-sm font-semibold font-[Chivo]">Supporting Images</h3>
            <Button size="sm" variant="outline" onClick={() => imageInputRef.current?.click()} disabled={uploadingImage} data-testid="upload-images-btn">
              <UploadSimple size={14} className="mr-1" /> {uploadingImage ? "Uploading..." : "Upload Images"}
            </Button>
            <input ref={imageInputRef} type="file" className="hidden" accept=".png,.jpg,.jpeg,.webp,.heic" multiple onChange={e => handleImageUpload(e.target.files)} data-testid="image-file-input" />
          </div>
          {form.images.length === 0 ? (
            <div className="p-6 text-center text-muted-foreground text-xs cursor-pointer hover:bg-muted/10 transition-colors" onClick={() => imageInputRef.current?.click()}>
              Click to upload photos to support this invoice (before/after, site photos, damage, etc.)
            </div>
          ) : (
            <div className="p-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {form.images.map((img, idx) => (
                <div key={img.id || idx} className="border border-border rounded-lg overflow-hidden bg-muted/10" data-testid={`invoice-image-${idx}`}>
                  <div className="aspect-video bg-muted flex items-center justify-center overflow-hidden">
                    <img
                      src={img.file_url?.startsWith("http") ? img.file_url : `${process.env.REACT_APP_BACKEND_URL}${img.file_url}`}
                      alt={img.label || "Invoice image"}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="p-2 space-y-1.5">
                    <Input
                      value={img.label || ""}
                      onChange={e => updateImage(idx, "label", e.target.value)}
                      placeholder="Image label (e.g. Damaged boiler)"
                      className="h-7 text-xs"
                      data-testid={`image-label-${idx}`}
                    />
                    <Select value={img.linked_item || "none"} onValueChange={v => updateImage(idx, "linked_item", v === "none" ? "" : v)}>
                      <SelectTrigger className="h-7 text-xs" data-testid={`image-link-${idx}`}><SelectValue placeholder="Link to item..." /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">No linked item</SelectItem>
                        {form.line_items.map((li, i) => (
                          <SelectItem key={li.id || i} value={li.item_name || li.description || `Item ${i + 1}`}>
                            {li.item_name || li.description || `Item ${i + 1}`}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button variant="ghost" size="sm" className="h-6 text-[10px] text-destructive w-full" onClick={() => removeImage(idx)} data-testid={`remove-image-${idx}`}>
                      <Trash size={12} className="mr-1" /> Remove
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Bank Payment Details */}
        <div className="border border-border rounded-lg p-4 space-y-3" data-testid="invoice-bank-details">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h3 className="text-sm font-semibold font-[Chivo]">Bank Payment Details</h3>
              <p className="text-[11px] text-muted-foreground">Shown on the client invoice PDF. Defaults loaded from Settings → Bank Details — edit per-invoice if needed.</p>
            </div>
            <Button type="button" size="sm" variant="outline" className="text-xs h-7" onClick={async () => {
              try {
                const { data } = await API.get("/settings/bank-details");
                setForm(f => ({
                  ...f,
                  bank_account_name: data.account_name || "",
                  bank_sort_code: data.sort_code || "",
                  bank_account_number: data.account_number || "",
                  bank_name: data.bank_name || "",
                  bank_reference_label: data.reference_label || "",
                  payment_instructions: data.payment_instructions || "",
                }));
                toast.success("Loaded defaults from Settings");
              } catch { toast.error("Failed to load defaults"); }
            }} data-testid="invoice-bank-load-defaults">Load defaults</Button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="sm:col-span-2">
              <Label className="text-xs">Account Name</Label>
              <Input value={form.bank_account_name} onChange={e => setForm(f => ({ ...f, bank_account_name: e.target.value }))} placeholder="Your Company Ltd" className="mt-1" data-testid="invoice-bank-account-name" />
            </div>
            <div>
              <Label className="text-xs">Sort Code</Label>
              <Input value={form.bank_sort_code} onChange={e => {
                const digits = e.target.value.replace(/\D/g, "").slice(0, 6);
                let formatted = digits;
                if (digits.length > 4) formatted = `${digits.slice(0,2)}-${digits.slice(2,4)}-${digits.slice(4)}`;
                else if (digits.length > 2) formatted = `${digits.slice(0,2)}-${digits.slice(2)}`;
                setForm(f => ({ ...f, bank_sort_code: formatted }));
              }} placeholder="00-00-00" maxLength={8} className="mt-1" data-testid="invoice-bank-sort-code" />
            </div>
            <div>
              <Label className="text-xs">Account Number</Label>
              <Input value={form.bank_account_number} onChange={e => setForm(f => ({ ...f, bank_account_number: e.target.value.replace(/\D/g, "").slice(0, 8) }))} placeholder="12345678" maxLength={8} className="mt-1" data-testid="invoice-bank-account-number" />
            </div>
            <div>
              <Label className="text-xs">Bank Name (optional)</Label>
              <Input value={form.bank_name} onChange={e => setForm(f => ({ ...f, bank_name: e.target.value }))} placeholder="Barclays" className="mt-1" data-testid="invoice-bank-name" />
            </div>
            <div>
              <Label className="text-xs">Payment Reference (optional)</Label>
              <Input value={form.bank_reference_label} onChange={e => setForm(f => ({ ...f, bank_reference_label: e.target.value }))} placeholder={invoice?.reference || "Invoice reference"} className="mt-1" data-testid="invoice-bank-reference" />
            </div>
            <div className="sm:col-span-2">
              <Label className="text-xs">Payment Instructions (optional)</Label>
              <Textarea value={form.payment_instructions} onChange={e => setForm(f => ({ ...f, payment_instructions: e.target.value }))} rows={2} placeholder="e.g. Please quote the invoice reference on your transfer. BACS payments take 1–3 working days." className="mt-1 text-sm" data-testid="invoice-payment-instructions" />
              <p className="text-[10px] text-muted-foreground mt-1">Shown on the client invoice PDF directly under the bank details.</p>
            </div>
          </div>
        </div>

        {/* Terms & Payment Due */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="border border-border rounded-lg p-4 space-y-3">
            <h3 className="text-sm font-semibold font-[Chivo]">Terms & Conditions</h3>
            <Textarea value={form.terms} onChange={e => setForm(f => ({ ...f, terms: e.target.value }))} rows={4} placeholder="Payment terms, caveats, conditions..." className="text-xs" data-testid="invoice-terms" />
          </div>
          <div className="border border-border rounded-lg p-4 space-y-3">
            <h3 className="text-sm font-semibold font-[Chivo]">Additional Info</h3>
            <div>
              <Label className="text-xs">Payment due (days)</Label>
              <Input type="number" value={form.due_days} onChange={e => setForm(f => ({ ...f, due_days: parseInt(e.target.value) || 30 }))} className="w-24 mt-1" data-testid="invoice-validity" />
            </div>
            <div>
              <Label className="text-xs">Internal Notes</Label>
              <Textarea value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} rows={2} placeholder="Internal notes (not shown on client PDF)" className="text-xs mt-1" data-testid="invoice-notes" />
            </div>
          </div>
        </div>

        {/* Save */}
        <div className="flex items-center justify-end gap-3 pt-2">
          <Button variant="outline" onClick={() => navigate("/invoices")}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving} data-testid="save-invoice-btn">
            {saving ? "Saving..." : isNew ? "Create Invoice" : "Save Changes"}
          </Button>
        </div>
      </div>

      {/* Send to Client Portal */}
      <Dialog open={showPortalDialog} onOpenChange={setShowPortalDialog}>
        <DialogContent className="max-w-md" data-testid="send-portal-dialog">
          <DialogHeader>
            <DialogTitle className="font-[Chivo] flex items-center gap-2"><PaperPlaneTilt size={18} /> Send to Client Portal</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <p className="text-muted-foreground text-xs">
              Publishes <strong>{invoice?.reference}</strong> ({`£${(invoice?.total_inc_vat ?? invoice?.grand_total ?? 0).toLocaleString("en-GB", { minimumFractionDigits: 2 })}`}) to <strong>{invoice?.client_company || invoice?.client_name}</strong>'s portal{invoice?.status === "draft" ? " and marks it as Sent" : ""}. Every active portal user gets an in-app alert and a branded email with a "View Invoice" link.
            </p>
            <div className="border border-border rounded-md p-2.5 text-xs" data-testid="portal-recipients">
              {portalUsers === null ? <span className="text-muted-foreground">Checking portal users…</span>
                : portalUsers.length === 0 ? <span className="text-amber-700 dark:text-amber-400">No active portal users for this client yet — the invoice will still be published, but nobody will be emailed. Invite users from the client's page.</span>
                : <><span className="font-semibold">{portalUsers.length} recipient{portalUsers.length !== 1 ? "s" : ""}:</span> {portalUsers.map(u => u.name || u.email).join(", ")}</>}
            </div>
            <div>
              <Label className="text-xs">Message (optional)</Label>
              <Textarea rows={3} value={portalMsg} onChange={(e) => setPortalMsg(e.target.value)} placeholder="e.g. Thanks for your business — payment is due within 30 days." className="mt-1 text-xs" data-testid="portal-message" />
            </div>
            <label className="flex items-center gap-2 text-xs cursor-pointer">
              <input type="checkbox" checked={portalAttach} onChange={(e) => setPortalAttach(e.target.checked)} data-testid="portal-attach-pdf" /> Attach the client-branded PDF to the email
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setShowPortalDialog(false)} data-testid="portal-cancel-btn">Cancel</Button>
            <Button size="sm" onClick={handleSendToPortal} disabled={portalSending} data-testid="portal-send-btn"><PaperPlaneTilt size={14} className="mr-1" /> {portalSending ? "Sending…" : invoice?.portal_sent_at ? "Resend" : "Send to Portal"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Convert Dialog */}
      <Dialog open={showConvert} onOpenChange={setShowConvert}>
        <DialogContent className="max-w-sm" data-testid="convert-dialog">
          <DialogHeader>
            <DialogTitle className="font-[Chivo]">Convert Invoice</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground mb-4">Convert this invoice to a Job or a Project. Converting to a Project will create a task for each line item.</p>
          <div className="space-y-2">
            <Button className="w-full justify-start" variant="outline" onClick={() => handleConvert("job")} disabled={converting} data-testid="convert-to-job-btn">
              <Briefcase size={18} className="mr-2 text-blue-500" /> Convert to Job
            </Button>
            <Button className="w-full justify-start" variant="outline" onClick={() => handleConvert("project")} disabled={converting} data-testid="convert-to-project-btn">
              <Folder size={18} className="mr-2 text-emerald-500" /> Convert to Project
              <span className="text-[10px] text-muted-foreground ml-auto">{form.line_items.length} task{form.line_items.length !== 1 ? "s" : ""}</span>
            </Button>
          </div>
          <DialogFooter>
            <Button variant="ghost" size="sm" onClick={() => setShowConvert(false)}>Cancel</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Email Invoice Dialog (opt-in white-label feature) */}
      <Dialog open={showEmailDialog} onOpenChange={setShowEmailDialog}>
        <DialogContent className="max-w-lg" data-testid="email-invoice-dialog">
          <DialogHeader>
            <DialogTitle className="font-[Chivo]">Email Invoice to Client</DialogTitle>
          </DialogHeader>
          <p className="text-xs text-muted-foreground mb-2">
            A branded PDF of this invoice will be attached automatically. Client branding (logo, company name, footer) is picked up from this client's saved branding profile.
          </p>
          {emailInfo.send_count > 0 && (
            <div className="bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-900 rounded-md px-3 py-2 text-[11px] text-blue-700 dark:text-blue-300" data-testid="email-history-banner">
              Sent {emailInfo.send_count} time{emailInfo.send_count !== 1 ? "s" : ""}
              {emailInfo.last_sent_at ? ` · last on ${new Date(emailInfo.last_sent_at).toLocaleString("en-GB")}` : ""}
            </div>
          )}

          <div className="space-y-3 mt-2">
            <div>
              <Label className="text-xs font-semibold">To *</Label>
              <Input type="email" value={emailForm.to_email} onChange={e => setEmailForm(f => ({ ...f, to_email: e.target.value }))} placeholder="client@company.com" className="mt-1" data-testid="email-to-input" />
            </div>
            <div>
              <Label className="text-xs font-semibold">CC (comma separated)</Label>
              <Input value={emailForm.cc_emails} onChange={e => setEmailForm(f => ({ ...f, cc_emails: e.target.value }))} placeholder="optional@example.com, another@example.com" className="mt-1" data-testid="email-cc-input" />
            </div>
            <div>
              <Label className="text-xs font-semibold">Subject *</Label>
              <Input value={emailForm.subject} onChange={e => setEmailForm(f => ({ ...f, subject: e.target.value }))} className="mt-1" data-testid="email-subject-input" />
            </div>
            <div>
              <Label className="text-xs font-semibold">Message</Label>
              <Textarea value={emailForm.message} onChange={e => setEmailForm(f => ({ ...f, message: e.target.value }))} rows={10} className="mt-1 text-xs" data-testid="email-message-input" />
            </div>
            {emailForm.branding?.company_name && (
              <p className="text-[10px] text-emerald-600 dark:text-emerald-400">
                <CheckCircle size={10} className="inline mr-1" weight="fill" />
                PDF will be branded as "{emailForm.branding.company_name}"
              </p>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setShowEmailDialog(false)} data-testid="email-cancel-btn">Cancel</Button>
            <Button size="sm" onClick={handleSendEmail} disabled={emailSending} data-testid="email-send-btn">
              <EnvelopeSimple size={14} className="mr-1" /> {emailSending ? "Sending..." : "Send Email"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reverse pricing calculator */}
      <ReverseCalcDialog
        open={reverseCalcOpen}
        onOpenChange={setReverseCalcOpen}
        initialMarkupA={form.markup_a_pct || 0}
        initialMarkupB={form.markup_b_pct || 0}
        initialMarkupALabel={form.markup_a_label || "Markup A"}
        initialMarkupBLabel={form.markup_b_label || "Markup B"}
        initialVatEnabled={!!form.vat_enabled}
        initialVatRate={form.vat_rate || 20}
        initialTotal={Number(totalIncVat) > 0 ? Number(totalIncVat) : 1000}
        contextLabel={form.reference ? `${form.reference} · ${form.markup_a_pct || 0}% + ${form.markup_b_pct || 0}%` : ""}
      />
    </div>
  );
}
